// Share Link Module for Configuration Sharing

/**
 * Generate a shareable URL with current configuration
 */
export function generateShareLink(state) {
    const baseUrl = window.location.origin + window.location.pathname;
    const params = new URLSearchParams();
    
    // Add language parameter if i18n is available
    if (typeof window.i18n !== 'undefined' && window.i18n.getCurrentLanguage) {
        params.set('lang', window.i18n.getCurrentLanguage());
    }
    
    // Add all configuration parameters
    params.set('width', state.width);
    params.set('depth', state.depth);
    params.set('verandaType', state.verandaType);
    params.set('color', state.color);
    params.set('covering', state.covering);
    params.set('finishingCap', state.finishingCap);
    params.set('led', state.led);
    params.set('leftWall', state.leftWall);
    params.set('rightWall', state.rightWall);
    params.set('frontWall', state.frontWall);
    params.set('houseType', state.houseType || '0');
    
    // Extra options
    if (state.markiezen) params.set('markiezen', '1');
    if (state.screens) params.set('screens', '1');
    if (state.vasteKozijnen) params.set('vasteKozijnen', '1');
    
    return `${baseUrl}?${params.toString()}`;
}

/**
 * Parse URL parameters and restore configuration
 */
export function loadConfigurationFromURL() {
    const params = new URLSearchParams(window.location.search);
    
    if (!params.has('width')) {
        return null; // No configuration in URL
    }
    
    const config = {
        width: parseFloat(params.get('width')) || 6,
        depth: parseFloat(params.get('depth')) || 3,
        verandaType: params.get('verandaType') || '0',
        color: params.get('color') || '383e42',
        covering: params.get('covering') || '0',
        finishingCap: params.get('finishingCap') || '0',
        led: params.get('led') || '0',
        leftWall: params.get('leftWall') || '0',
        rightWall: params.get('rightWall') || '0',
        frontWall: params.get('frontWall') || '0',
        houseType: params.get('houseType') || '2',  // Default Vrijstaand
        markiezen: params.get('markiezen') === '1',
        screens: params.get('screens') === '1',
        vasteKozijnen: params.get('vasteKozijnen') === '1'
    };
    
    return config;
}

/**
 * Copy share link to clipboard
 */
export async function copyShareLink(state) {
    const shareLink = generateShareLink(state);
    
    try {
        await navigator.clipboard.writeText(shareLink);
        return { success: true, link: shareLink };
    } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        
        try {
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return { success: true, link: shareLink };
        } catch (err2) {
            document.body.removeChild(textArea);
            return { success: false, error: err2 };
        }
    }
}

/**
 * Geef de render-image door aan de backend.
 *
 * De render-server (ws.cloudrenderer.eu) stuurt geen CORS-headers, dus
 * `fetch()` of een canvas-readback in de browser werkt niet. We sturen de
 * URL, dan downloadt PHP de afbeelding server-side (geen CORS-restrictie).
 *
 * Prioriteert de quote-modal image (viewMode 0 front view) en valt terug
 * op de widget image als die er nog niet is.
 */
export async function captureRenderImage() {
    let imgElement = document.getElementById('quote-preview-image');

    if (!imgElement || !imgElement.src || !imgElement.complete || imgElement.naturalHeight === 0) {
        imgElement = document.getElementById('widget-preview-image');
    }

    if (!imgElement || !imgElement.src) {
        console.warn('No preview image found');
        return null;
    }

    if (!imgElement.complete || imgElement.naturalHeight === 0) {
        console.warn('Image not yet loaded');
        return null;
    }

    // Render-server stuurt nu CORS-headers, dus we kunnen de bytes zelf
    // ophalen en als data-URI doorgeven aan PHP. Die embedt 'm dan
    // permanent in de mail (geen externe afhankelijkheid op uptime/retentie
    // van de render-server). Bij een fout vallen we terug op de bare URL —
    // PHP's resolveRenderImage handelt beide vormen.
    try {
        const response = await fetch(imgElement.src);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (err) {
        console.warn('captureRenderImage: fetch faalde, val terug op URL', err);
        return imgElement.src;
    }
}
